// src/layouts/documents/DocumentViewer.js

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import Loader from "components/Loader/Loader";
import { TextField } from "@mui/material";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";

import { Document, Page, pdfjs } from "react-pdf";
import { IconButton, Tabs, Tab, Box, Grid, Card, Tooltip, Select, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ArrowBack,
  ArrowForward,
  ZoomIn,
  ZoomOut,
  Delete,
  Download,
  Print,
  CheckCircle,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Import your API instance
import api from "../../services/api";

// Import React Query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import InvoiceDetails from "common/DocumentViewer/InvoiceDetails/InvoiceDetails";
import InvoiceLineItems from "common/DocumentViewer/InvoiceLineItems/InvoiceLineItems";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function DocumentViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedCompany } = useCompany(); // Pridėkite šią eilutę čia
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  // Local state for document data
  const [documentData, setDocumentData] = useState(null);
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([]);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [showFieldMarkers, setShowFieldMarkers] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeFieldEditor, setActiveFieldEditor] = useState(null);
  const [fieldEditorValue, setFieldEditorValue] = useState("");
  const [validationFeedback, setValidationFeedback] = useState([]);

  // Zoom and page control functions
  const handleZoomIn = () => setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3.0));
  const handleZoomOut = () => setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  const goToPrevPage = () => setPageNumber((prevPageNumber) => Math.max(prevPageNumber - 1, 1));
  const goToNextPage = () =>
    setPageNumber((prevPageNumber) => Math.min(prevPageNumber + 1, numPages));

  // Function to handle container size
  const handleResize = () => {
    const container = document.getElementById("pdf-container");
    if (container) {
      setContainerWidth(container.clientWidth);
    }
  };

  // Listen to window resize events
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    // Also try to set it immediately and on a small timeout to catch the render
    handleResize();
    setTimeout(handleResize, 100);
    setTimeout(handleResize, 500);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    handleResize(); // <-- Catch the width immediately when the PDF container appears!
  };

  // UseQuery to fetch document data
  const { data, isLoading, error } = useQuery({
    queryKey: ["document", id],
    queryFn: async () => {
      try {
        console.log(`Fetching document data for ID: ${id}`);
        const response = await api.get(`/api/documents/${id}/`);
        console.log("Document data fetched:", response.data);
        return response.data;
      } catch (err) {
        console.error("Error fetching document data:", err);
        throw err;
      }
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
  });

  // Update local state when data changes
  useEffect(() => {
    if (data) {
      console.log("Updating state with document data:", data);
      setDocumentData({
        ...data,
        data: data.data || {},
        field_locations: data.field_locations || [],
      });
      setNotes(data.notes || "");
      setLineItems(data.line_items || []);
    }
  }, [data]);

  // Mutation: Save Notes
  const saveNotesMutation = useMutation({
    mutationFn: async (newNotes) => {
      console.log("Saving notes:", newNotes);
      const response = await api.post(`/api/documents/${id}/save_notes/`, { notes: newNotes });
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("notesSavedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving notes:", error);
      showSnackbar(t("errorSavingNotes"), "error");
    },
  });

  // Mutation: Save Details
  const saveDetailsMutation = useMutation({
    mutationFn: async (updatedData) => {
      console.log("Saving document details:", updatedData);

      const formattedData = { ...updatedData };
      const amountFields = ["net_amount", "total_amount", "total_tax_amount"];
      amountFields.forEach((field) => {
        if (formattedData[field] && !isNaN(parseFloat(formattedData[field]))) {
          formattedData[field] = parseFloat(formattedData[field]).toFixed(2);
        }
      });

      const response = await api.post(`/api/documents/${id}/update-data/`, formattedData);
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("informationSavedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving document details:", error);
      showSnackbar(t("errorSavingInformation"), "error");
    },
  });

  // Mutation: Save Line Items
  const saveLineItemsMutation = useMutation({
    mutationFn: async (updatedLineItems) => {
      console.log("Saving line items:", updatedLineItems);

      const formattedLineItems = updatedLineItems.map((item) => {
        const qty =
          item.quantity && !isNaN(parseFloat(item.quantity)) ? parseFloat(item.quantity) : null;
        const price =
          item.unit_price && !isNaN(parseFloat(item.unit_price))
            ? parseFloat(item.unit_price)
            : null;
        const total =
          item.total_amount && !isNaN(parseFloat(item.total_amount))
            ? parseFloat(item.total_amount)
            : null;

        return {
          ...item,
          quantity: qty !== null ? qty.toFixed(2) : null,
          unit_price: price !== null ? price.toFixed(4) : null,
          total_amount: total !== null ? total.toFixed(2) : null,
        };
      });

      const response = await api.post(`/api/documents/${id}/update-line-items/`, {
        line_items: formattedLineItems,
      });
      return response.data;
    },
    onSuccess: (data) => {
      showSnackbar(t("lineItemsSavedSuccessfully"), "success");

      // Jei backend grąžino atnaujintą dokumentą, atnaujinkime cache
      if (data.document) {
        queryClient.setQueryData(["document", id], data.document);
        // Atnaujinkime ir local state
        setDocumentData({
          ...data.document,
          data: data.document.data || {},
          field_locations: data.document.field_locations || [],
        });
        setLineItems(data.document.line_items || []);
      } else {
        // Jei ne, tiesiog invaliduokime
        queryClient.invalidateQueries(["document", id]);
      }

      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving line items:", error);
      showSnackbar(t("errorSavingLineItems"), "error");
    },
  });

  // Mutation: Recognize Document
  const recognizeMutation = useMutation({
    mutationFn: async () => {
      console.log("Recognizing document with ID:", id);
      const response = await api.post(`/api/recognize/`, {
        document_ids: [id],
        force_recognize: true,
      });
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("documentRecognizedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error recognizing document:", error);
      showSnackbar(t("errorRecognizingDocument"), "error");
    },
  });

  // Mutation: Delete Document
  const deleteMutation = useMutation({
    mutationFn: async () => {
      console.log("Deleting document with ID:", id);
      const response = await api.delete(`/api/documents/${id}/`);
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("documentDeletedSuccessfully"), "success");
      navigate("/documents");
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error deleting document:", error);
      showSnackbar(t("errorDeletingDocument"), "error");
    },
  });

  // Download document (no mutation needed)
  const handleDownload = async () => {
    console.log("Downloading document:", documentData.file_url);

    let downloadName = documentData.title || "document";
    const knownExtensions = [".pdf", ".jpg", ".jpeg", ".png"];
    const hasKnownExtension = knownExtensions.some((ext) =>
      downloadName.toLowerCase().endsWith(ext)
    );

    if (!hasKnownExtension) {
      downloadName += ".pdf"; // Pridedame .pdf jei pabaigoje nėra oficialaus formato
    }

    try {
      // Fetching the file as a Blob to enforce the custom filename.
      // `link.download` attribute is ignored by browsers if the file is cross-origin.
      const response = await fetch(documentData.file_url);
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadName; // Dabar naršyklė garantuotai naudos šį pavadinimą
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.warn("Fetch missing CORS or failed, falling back to basic link.", error);
      const link = document.createElement("a");
      link.href = documentData.file_url;
      link.download = downloadName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Print document (no mutation needed)
  const handlePrint = () => {
    console.log("Printing document:", documentData.file_url);
    const printWindow = window.open(documentData.file_url, "_blank");
    printWindow.focus();
    printWindow.print();
  };

  useEffect(() => {
    if (!showFieldMarkers) {
      setActiveFieldEditor(null);
    }
  }, [showFieldMarkers]);

  useEffect(() => {
    setActiveFieldEditor((prev) =>
      prev && Number(prev.page_number) !== Number(pageNumber) ? null : prev
    );
  }, [pageNumber]);

  // Reset PDF viewer state every time the document ID changes so react-pdf
  // starts clean and never gets stuck on the previous document's "Loading page…".
  // If react-query already has this document cached, populate documentData
  // synchronously so the full-screen Loader never flashes for cached docs.
  // Only set documentData=null (→ Loader) when the doc genuinely isn't cached yet.
  useEffect(() => {
    const cached = queryClient.getQueryData(["document", id]);
    if (cached) {
      setDocumentData({
        ...cached,
        data: cached.data || {},
        field_locations: cached.field_locations || [],
      });
      setNotes(cached.notes || "");
      setLineItems(cached.line_items || []);
    } else {
      setDocumentData(null);
    }
    setNumPages(null);
    setPageNumber(1);
    setActiveFieldEditor(null);
    setValidationFeedback([]);
  }, [id]);

  // Dependencies: query status
  if (error) {
    console.error("Error in DocumentViewer:", error);
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="error">
            Error fetching document data
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (isLoading || !documentData) {
    console.log("Loading document data...");
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <Loader fullscreen />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  console.log("DocumentViewer - Document data is valid:", documentData);

  const zoomPresets = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0];
  const zoomOptions = [...new Set([...zoomPresets, zoom])].sort((a, b) => a - b);
  const uploaderDisplay =
    documentData.uploaded_by_name || documentData.uploaded_by_email || t("unknownUploader");
  const rawRecognizedText = documentData.recognized_text;
  const prevDocumentId = documentData.prev_document_id;
  const nextDocumentId = documentData.next_document_id;
  const hasRecognizedText =
    typeof rawRecognizedText === "string"
      ? rawRecognizedText.trim() !== "" && rawRecognizedText.trim().toLowerCase() !== "false"
      : Boolean(rawRecognizedText);
  const recognizedTextToShow = hasRecognizedText
    ? rawRecognizedText
    : t("noRecognizedTextAvailable");
  const currentFieldLocations = (documentData.field_locations || []).filter(
    (location) => Number(location.page_number) === Number(pageNumber)
  );

  const getFieldLabel = (location) => t(location?.label_key) || location?.field || t("details");

  const normalizeFieldValue = (fieldName, rawValue) => {
    const value = String(rawValue ?? "").trim();
    if (["total_amount", "net_amount", "total_tax_amount"].includes(fieldName)) {
      return value.replace(/\s+/g, "").replace(",", ".");
    }
    return value;
  };

  const parseAmount = (value) => {
    const normalized = normalizeFieldValue("total_amount", value);
    if (!normalized) {
      return null;
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parseDateValue = (value) => {
    if (!value) {
      return null;
    }
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const buildRelatedValidation = (updatedData, editedField) => {
    const messages = [];

    if (["total_amount", "net_amount", "total_tax_amount"].includes(editedField)) {
      const totalAmount = parseAmount(updatedData?.total_amount);
      const netAmount = parseAmount(updatedData?.net_amount);
      const taxAmount = parseAmount(updatedData?.total_tax_amount);

      if (totalAmount !== null && netAmount !== null && taxAmount !== null) {
        const expectedTotal = Number((netAmount + taxAmount).toFixed(2));
        const actualTotal = Number(totalAmount.toFixed(2));
        const totalsMatch = Math.abs(expectedTotal - actualTotal) <= 0.01;

        messages.push({
          severity: totalsMatch ? "success" : "warning",
          text: totalsMatch
            ? `${t("validationTotalsMatch")} (${netAmount.toFixed(2)} + ${taxAmount.toFixed(
                2
              )} = ${actualTotal.toFixed(2)})`
            : `${t("validationTotalsMismatch")} (${netAmount.toFixed(2)} + ${taxAmount.toFixed(
                2
              )} = ${expectedTotal.toFixed(2)}, ${t("totalAmount")}: ${actualTotal.toFixed(2)})`,
        });
      }
    }

    if (["invoice_date", "due_date"].includes(editedField)) {
      const invoiceDate = parseDateValue(updatedData?.invoice_date);
      const dueDate = parseDateValue(updatedData?.due_date);

      if (invoiceDate && dueDate) {
        const datesValid = dueDate >= invoiceDate;
        messages.push({
          severity: datesValid ? "success" : "warning",
          text: datesValid
            ? t("validationDatesMatch")
            : `${t("validationDatesMismatch")} (${t("invoiceDate")}: ${
                updatedData?.invoice_date || "-"
              }, ${t("dueDate")}: ${updatedData?.due_date || "-"})`,
        });
      }
    }

    if (!messages.length) {
      messages.push({
        severity: "info",
        text: t("noRelatedValidationRules"),
      });
    }

    return messages;
  };

  const handleFieldOverlayClick = (location) => {
    if (!location?.field || !documentData?.data) {
      return;
    }

    const currentValue = documentData.data[location.field] ?? "";
    setActiveFieldEditor({
      ...location,
      label: getFieldLabel(location),
    });
    setFieldEditorValue(String(currentValue));
  };

  const handleInlineEditorClose = () => {
    setActiveFieldEditor(null);
    setFieldEditorValue("");
  };

  const handleInlineFieldSave = () => {
    if (!activeFieldEditor?.field || !documentData?.data) {
      return;
    }

    const fieldName = activeFieldEditor.field;
    const newValue = normalizeFieldValue(fieldName, fieldEditorValue);
    const previousDocumentState = documentData;
    const updatedData = {
      ...documentData.data,
      [fieldName]: newValue,
    };

    const nextDocumentState = {
      ...documentData,
      data: updatedData,
      field_locations: (documentData.field_locations || []).map((item) =>
        item.field === fieldName ? { ...item, value: newValue } : item
      ),
    };

    setDocumentData(nextDocumentState);
    setValidationFeedback(buildRelatedValidation(updatedData, fieldName));

    saveDetailsMutation.mutate(updatedData, {
      onError: () => {
        setDocumentData(previousDocumentState);
      },
    });

    handleInlineEditorClose();
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={2}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={isMdDown ? "flex-start" : "center"}
                  flexDirection={isMdDown ? "column" : "row"}
                  gap={isMdDown ? 1 : 0}
                  mb={2}
                  sx={{ minWidth: 0 }}
                >
                  <MDBox display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() => navigate("/documents")}
                      startIcon={<ArrowBack />}
                    >
                      {t("backToDocuments")}
                    </MDButton>
                    {!isMdDown && (
                      <MDTypography
                        variant="h5"
                        fontWeight="medium"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          maxWidth: "50vw",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={documentData.title || ""}
                      >
                        {documentData.title}
                      </MDTypography>
                    )}
                  </MDBox>
                  <Box sx={{ flex: 1, mr: "auto", ml: isMdDown ? 0 : 2, mb: isMdDown ? 1 : 0 }} />
                  {isMdDown && (
                    <MDTypography
                      variant="subtitle1"
                      fontWeight="medium"
                      sx={{
                        width: "100%",
                        mt: 0.5,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                      title={documentData.title || ""}
                    >
                      {documentData.title}
                    </MDTypography>
                  )}
                  <MDBox display="flex" gap={1} flexWrap={isMdDown ? "wrap" : "nowrap"}>
                    <Tooltip title={t("previousDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          color="info"
                          disabled={!prevDocumentId}
                          onClick={() => prevDocumentId && navigate(`/documents/${prevDocumentId}`)}
                        >
                          <ArrowBack />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("nextDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          color="info"
                          disabled={!nextDocumentId}
                          onClick={() => nextDocumentId && navigate(`/documents/${nextDocumentId}`)}
                        >
                          <ArrowForward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("recognizeDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <MDButton
                          variant="contained"
                          color="info"
                          onClick={() => recognizeMutation.mutate()}
                          disabled={recognizeMutation.isLoading}
                          startIcon={<CheckCircle />}
                        >
                          {recognizeMutation.isLoading ? t("processing") : t("recognize")}
                        </MDButton>
                      </span>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2}>
                        {isMdDown ? (
                          <Select
                            value={tabValue}
                            onChange={(e) => setTabValue(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{ mb: 2 }}
                            MenuProps={{
                              disableScrollLock: true,
                              PaperProps: { sx: { maxHeight: 360 } },
                            }}
                          >
                            <MenuItem value={0}>{t("documentDetails")}</MenuItem>
                            <MenuItem value={1}>{t("recognizedText")}</MenuItem>
                            <MenuItem value={2}>{t("notes")}</MenuItem>
                            <MenuItem value={3}>{t("lineItems")}</MenuItem>
                            <MenuItem value={4}>{t("formattedText")}</MenuItem>
                          </Select>
                        ) : (
                          <Tabs
                            value={tabValue}
                            onChange={(event, newValue) => setTabValue(newValue)}
                            variant="scrollable"
                            sx={{
                              borderBottom: 1,
                              borderColor: "divider",
                              mb: 2,
                            }}
                          >
                            <Tab label={t("documentDetails")} />
                            <Tab label={t("recognizedText")} />
                            <Tab label={t("notes")} />
                            <Tab label={t("lineItems")} />
                            <Tab label={t("formattedText")} />
                          </Tabs>
                        )}

                        <MDBox
                          sx={{
                            height: isMdDown ? "auto" : "calc(100vh - 400px)",
                            maxHeight: isMdDown ? "none" : "calc(100vh - 400px)",
                            overflowY: isMdDown ? "visible" : "auto",
                          }}
                        >
                          {tabValue === 0 && (
                            <MDBox>
                              <InvoiceDetails
                                documentData={documentData}
                                setDocumentData={setDocumentData}
                                handleDetailsSave={() => {
                                  saveDetailsMutation.mutate(documentData.data);
                                }}
                              />
                            </MDBox>
                          )}

                          {tabValue === 1 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("recognizedText")}
                              </MDTypography>
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  p: 2,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "divider",
                                  mb: documentData?.tables?.length > 0 ? 3 : 0,
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "inherit",
                                    margin: 0,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {recognizedTextToShow}
                                </pre>
                              </Box>
                              {documentData?.tables?.length > 0 && (
                                <MDBox>
                                  <MDTypography variant="h6" mb={1}>
                                    {t("extractedTables")}
                                  </MDTypography>
                                  {documentData.tables.map((table, tableIndex) => (
                                    <Box key={tableIndex} sx={{ mb: 2, overflowX: "auto" }}>
                                      <MDTypography
                                        variant="caption"
                                        color="text"
                                        mb={0.5}
                                        display="block"
                                      >
                                        {t("table")} {tableIndex + 1}
                                      </MDTypography>
                                      <table
                                        style={{
                                          borderCollapse: "collapse",
                                          width: "100%",
                                          fontSize: "0.8rem",
                                        }}
                                      >
                                        <tbody>
                                          {table.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                              {Array.isArray(row) ? (
                                                row.map((cell, cellIndex) => (
                                                  <td
                                                    key={cellIndex}
                                                    style={{
                                                      border: "1px solid #ccc",
                                                      padding: "4px 8px",
                                                      backgroundColor:
                                                        rowIndex === 0 ? "#f5f5f5" : "transparent",
                                                      fontWeight: rowIndex === 0 ? 600 : 400,
                                                      whiteSpace: "pre-wrap",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {cell}
                                                  </td>
                                                ))
                                              ) : (
                                                <td
                                                  style={{
                                                    border: "1px solid #ccc",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {row}
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </Box>
                                  ))}
                                </MDBox>
                              )}
                            </MDBox>
                          )}

                          {tabValue === 2 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("notes")}
                              </MDTypography>
                              <Card sx={{ mb: 2 }}>
                                <MDBox p={2}>
                                  <MDTypography variant="body2" color="text" fontWeight="medium">
                                    {t("uploadedBy")}: {uploaderDisplay}
                                  </MDTypography>
                                </MDBox>
                              </Card>
                              <TextField
                                label={t("notes")}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={6}
                                variant="outlined"
                                sx={{ mb: 2 }}
                              />
                              <MDButton
                                variant="contained"
                                color="info"
                                onClick={() => saveNotesMutation.mutate(notes)}
                                startIcon={<CheckCircle />}
                              >
                                {t("saveNotes")}
                              </MDButton>
                            </MDBox>
                          )}

                          {tabValue === 3 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("lineItems")}
                              </MDTypography>
                              <InvoiceLineItems
                                lineItems={lineItems}
                                onSave={(updatedLineItems) => {
                                  setLineItems(updatedLineItems);
                                  saveLineItemsMutation.mutate(updatedLineItems);
                                }}
                              />
                            </MDBox>
                          )}

                          {tabValue === 4 && (
                            <MDBox>
                              {documentData?.data && (
                                <Card sx={{ mb: 2 }}>
                                  <MDBox p={2}>
                                    <MDTypography variant="h6" mb={1}>
                                      {t("tokenUsage")}
                                    </MDTypography>
                                    <MDTypography variant="body2">
                                      {t("promptTokens")}: {documentData.data.prompt_tokens || 0}
                                    </MDTypography>
                                    <MDTypography variant="body2">
                                      {t("completionTokens")}:{" "}
                                      {documentData.data.completion_tokens || 0}
                                    </MDTypography>
                                    <MDTypography variant="body2" fontWeight="bold">
                                      {t("totalTokens")}: {documentData.data.total_tokens || 0}
                                    </MDTypography>
                                  </MDBox>
                                </Card>
                              )}
                              <MDTypography variant="h6" mb={2}>
                                {t("formattedText")}
                              </MDTypography>
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  p: 2,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "inherit",
                                    margin: 0,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {documentData?.formatted_text || t("noFormattedTextAvailable")}
                                </pre>
                              </Box>
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2}>
                        <MDTypography variant="h6" mb={2}>
                          {t("documentPreview")}
                        </MDTypography>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                          sx={{
                            backgroundColor: "background.paper",
                            p: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            flexWrap: isMdDown ? "wrap" : "nowrap",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <MDButton
                              variant={showFieldMarkers ? "contained" : "outlined"}
                              color="info"
                              size="small"
                              startIcon={showFieldMarkers ? <Visibility /> : <VisibilityOff />}
                              onClick={() => setShowFieldMarkers((prev) => !prev)}
                              sx={{ minWidth: 170 }}
                            >
                              {showFieldMarkers ? t("hideFieldMarkers") : t("showFieldMarkers")}
                            </MDButton>
                            <Tooltip title={t("zoomOut")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={handleZoomOut}
                                  size="small"
                                  disabled={zoom <= 0.5}
                                >
                                  <ZoomOut />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Select
                              value={zoom}
                              onChange={(e) => setZoom(e.target.value)}
                              size="small"
                              sx={{
                                minWidth: 90,
                                height: 32,
                                "& .MuiSelect-select": {
                                  paddingTop: "6px",
                                  paddingBottom: "6px",
                                  paddingLeft: "10px",
                                },
                              }}
                              MenuProps={{ disableScrollLock: true }}
                            >
                              {zoomOptions.map((z) => (
                                <MenuItem key={z} value={z}>
                                  {`${Math.round(z * 100)}%`}
                                </MenuItem>
                              ))}
                            </Select>
                            <Tooltip title={t("zoomIn")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={handleZoomIn}
                                  size="small"
                                  disabled={zoom >= 3.0}
                                >
                                  <ZoomIn />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t("previousPage")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={goToPrevPage}
                                  disabled={pageNumber <= 1}
                                  size="small"
                                >
                                  <ArrowBack />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <MDTypography variant="body2" component="span">
                              {t("page")} {pageNumber} {t("of")} {numPages}
                            </MDTypography>
                            <Tooltip title={t("nextPage")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={goToNextPage}
                                  disabled={pageNumber >= numPages}
                                  size="small"
                                >
                                  <ArrowForward />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </MDBox>
                          <MDBox display="flex" gap={1}>
                            <Tooltip title={t("deleteDocument")}>
                              <IconButton
                                onClick={() => {
                                  if (window.confirm(t("confirmDeleteDocument"))) {
                                    deleteMutation.mutate();
                                  }
                                }}
                                color="error"
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("downloadDocument")}>
                              <IconButton onClick={handleDownload} size="small">
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("printDocument")}>
                              <IconButton onClick={handlePrint} size="small">
                                <Print />
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                        <MDBox
                          sx={{
                            height: isMdDown ? "60vh" : "calc(100vh - 400px)",
                            overflow: "auto",
                            backgroundColor: "background.paper",
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            p: 2,
                          }}
                          id="pdf-container"
                        >
                          {documentData.file_url ? (
                            <Document
                              key={id}
                              file={documentData.file_url}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={(error) =>
                                console.error("Error loading document:", error)
                              }
                            >
                              {validationFeedback.length > 0 && (
                                <Card sx={{ mb: 2, border: 1, borderColor: "divider" }}>
                                  <MDBox p={1.5}>
                                    <MDTypography
                                      variant="button"
                                      fontWeight="medium"
                                      display="block"
                                      mb={0.5}
                                    >
                                      {t("relatedFieldValidation")}
                                    </MDTypography>
                                    {validationFeedback.map((item, index) => (
                                      <MDTypography
                                        key={`${item.severity}-${index}`}
                                        variant="caption"
                                        display="block"
                                        color={
                                          item.severity === "warning"
                                            ? "warning"
                                            : item.severity === "success"
                                            ? "success"
                                            : "text"
                                        }
                                        sx={{
                                          mb: index === validationFeedback.length - 1 ? 0 : 0.5,
                                        }}
                                      >
                                        {item.text}
                                      </MDTypography>
                                    ))}
                                  </MDBox>
                                </Card>
                              )}
                              <div style={{ position: "relative", display: "inline-block" }}>
                                <Page
                                  pageNumber={pageNumber}
                                  width={
                                    ((containerWidth || Math.min(window.innerWidth, 800)) - 40) *
                                    zoom
                                  }
                                />
                                {showFieldMarkers && currentFieldLocations.length > 0 && (
                                  <MDBox
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      pointerEvents: "auto",
                                      zIndex: 3,
                                    }}
                                  >
                                    {currentFieldLocations.map((location) => {
                                      const box = location.box || {};
                                      const left = Number(box.left) || 0;
                                      const top = Number(box.top) || 0;
                                      const width = Number(box.width) || 0;
                                      const height = Number(box.height) || 0;
                                      const fieldLabel = getFieldLabel(location);
                                      const labelTop =
                                        top > 3 ? Math.max(top - 2.7, 0.2) : top + height + 0.2;

                                      return (
                                        <React.Fragment
                                          key={`${location.field}-${location.page_number}`}
                                        >
                                          <MDBox
                                            title={`${fieldLabel}: ${location.value ?? ""}`}
                                            onClick={() => handleFieldOverlayClick(location)}
                                            onMouseDown={(event) => event.stopPropagation()}
                                            sx={{
                                              position: "absolute",
                                              left: `${left}%`,
                                              top: `${top}%`,
                                              width: `${Math.max(width, 1.6)}%`,
                                              height: `${Math.max(height, 0.8)}%`,
                                              border: (themeValue) =>
                                                `2px solid ${themeValue.palette.info.main}`,
                                              bgcolor: (themeValue) =>
                                                `${themeValue.palette.info.main}22`,
                                              borderRadius: 0.5,
                                              cursor: "pointer",
                                              pointerEvents: "auto",
                                              zIndex: 3,
                                              transition: "all 120ms ease",
                                              "&:hover": {
                                                bgcolor: (themeValue) =>
                                                  `${themeValue.palette.info.main}33`,
                                              },
                                            }}
                                          />
                                          <MDBox
                                            sx={{
                                              position: "absolute",
                                              left: `${left}%`,
                                              top: `${labelTop}%`,
                                              width: "max-content",
                                              maxWidth: "55%",
                                              px: 0,
                                              py: 0,
                                              bgcolor: "transparent",
                                              color: (themeValue) => themeValue.palette.info.main,
                                              pointerEvents: "none",
                                              overflow: "visible",
                                              textOverflow: "clip",
                                              whiteSpace: "normal",
                                              wordBreak: "break-word",
                                              fontSize: "0.75rem",
                                              fontWeight: 700,
                                              lineHeight: 1.2,
                                              textShadow: "0 0 1px rgba(255,255,255,0.9)",
                                              zIndex: 4,
                                            }}
                                            title={fieldLabel}
                                          >
                                            {fieldLabel}
                                          </MDBox>
                                        </React.Fragment>
                                      );
                                    })}
                                    {activeFieldEditor?.field &&
                                      Number(activeFieldEditor.page_number) ===
                                        Number(pageNumber) &&
                                      (() => {
                                        const editorBox = activeFieldEditor.box || {};
                                        const editorLeft = Number(editorBox.left) || 0;
                                        const editorTop = Number(editorBox.top) || 0;
                                        const editorHeight = Number(editorBox.height) || 0;
                                        const desiredTop = editorTop + editorHeight + 1.2;
                                        const fallbackTop = Math.max(editorTop - 13, 1);

                                        return (
                                          <MDBox
                                            sx={{
                                              position: "absolute",
                                              left: `${Math.min(editorLeft, 68)}%`,
                                              top: `${
                                                desiredTop <= 86 ? desiredTop : fallbackTop
                                              }%`,
                                              width: "min(280px, 42vw)",
                                              bgcolor: "background.paper",
                                              border: 1,
                                              borderColor: "info.main",
                                              borderRadius: 1,
                                              boxShadow: 6,
                                              p: 1.25,
                                              pointerEvents: "auto",
                                              zIndex: 4,
                                            }}
                                            onClick={(event) => event.stopPropagation()}
                                          >
                                            <MDTypography
                                              variant="caption"
                                              fontWeight="bold"
                                              display="block"
                                              mb={0.75}
                                            >
                                              {activeFieldEditor.label}
                                            </MDTypography>
                                            <TextField
                                              value={fieldEditorValue}
                                              onChange={(event) =>
                                                setFieldEditorValue(event.target.value)
                                              }
                                              onKeyDown={(event) => {
                                                if (event.key === "Enter" && !event.shiftKey) {
                                                  event.preventDefault();
                                                  handleInlineFieldSave();
                                                }
                                                if (event.key === "Escape") {
                                                  event.preventDefault();
                                                  handleInlineEditorClose();
                                                }
                                              }}
                                              fullWidth
                                              size="small"
                                              autoFocus
                                            />
                                            <MDBox
                                              display="flex"
                                              justifyContent="flex-end"
                                              gap={1}
                                              mt={1}
                                            >
                                              <MDButton
                                                variant="text"
                                                color="secondary"
                                                size="small"
                                                onClick={handleInlineEditorClose}
                                              >
                                                {t("cancel")}
                                              </MDButton>
                                              <MDButton
                                                variant="contained"
                                                color="info"
                                                size="small"
                                                onClick={handleInlineFieldSave}
                                                disabled={saveDetailsMutation.isLoading}
                                              >
                                                {saveDetailsMutation.isLoading
                                                  ? t("processing")
                                                  : t("save")}
                                              </MDButton>
                                            </MDBox>
                                          </MDBox>
                                        );
                                      })()}
                                  </MDBox>
                                )}
                              </div>
                            </Document>
                          ) : (
                            <MDTypography variant="body1" color="error">
                              {t("documentFileUrlMissing")}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DocumentViewer;
